import React from "react";
import { styled, useTheme } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { AppWrap, ContentArea, GridArea } from "../common/commonComps";
import { BigCarousel, SmallCarousel, ImagePop, LogoBox, BigImage, CategoryBoxSmall } from "./subComps";

import { getDisplayableCells, scrollerTo } from "../common/utils";
import { popMargins, iconSizes, iconMinMargins, TOPBARHEIGHT, MAX_USABLE_DISPLAY_WIDTH } from "../common/constants";
import programSpecificComponents from "./programSpecificComponents";
import { ClickableText, H5 } from "./account/components/Typography";
import type { ContextValue, Store, PromotedOffer, AppContext } from "../types";

const HomeHeader = styled(H5)(() => ({
    marginBottom: 15,
    marginTop: 40,
    color: `#333`,
    fontWeight: 600,
    fontSize: `25px`,
}));

const useCustomComponent = (
    use: boolean | PromotedOffer[] | undefined,
    deflt: React.ReactNode,
    {
        cx,
        component,
    }: { cx: ContextValue & { setStore?: (v: Store | null) => void }; component: string; editable?: boolean }
): React.ReactNode => {
    if (!use) return deflt;
    // cx has all ProgramComponentContext fields (context, size, viewportWidth, setStore) at runtime
    const ctx: Record<string, unknown> = { ...cx };
    return programSpecificComponents({
        context: ctx as Parameters<typeof programSpecificComponents>[0]["context"],
        component,
    });
};

const Home = (cx: ContextValue) => {
    const { setStore } = useOutletContext<{ setStore: (v: Store | null) => void }>();
    const {
        size,
        storeCellsAcross,
        contentAreaWidth,
        loaded,
        context = {} as AppContext,
        setContext,
        viewportWidth,
    } = cx;
    const { featured = [], popular = [], categories = [], promotedOffers } = context;
    const theme = useTheme();

    const editable = !!sessionStorage.getItem(`edit`);

    const parentWidth = Math.min(MAX_USABLE_DISPLAY_WIDTH, viewportWidth);
    // sizes for iconbox

    // sizes for featured
    const pmargin = popMargins[size];

    const pwidth = (parentWidth - pmargin * (size === `small` ? 2 : 4) * 2) / (size === `small` ? 2 : 4);
    const ipopprops = { pwidth, pmargin, editable, size, loaded, setContext, ...context };
    // sizes for category icons
    const iconsAcross = Math.trunc(parentWidth / (iconSizes[size] + iconMinMargins[size] * 2));
    const iconsLeftoverSpace = Math.trunc(parentWidth % (iconSizes[size] + iconMinMargins[size] * 2));
    const iconMargins = Math.trunc(iconsLeftoverSpace / iconsAcross / 2) + iconMinMargins[size];

    return loaded ? (
        <AppWrap style={{ paddingTop: `${TOPBARHEIGHT + 24}px` }} id="appwrap">
            <ContentArea id="contentarea" className="between">
                {useCustomComponent(
                    context.adminConfig?.mall?.useCustomBigCarousel,
                    <BigCarousel size={size} editable={editable} />,
                    {
                        editable,
                        cx,
                        component: `CustomBigCarousel`,
                    }
                )}
                {useCustomComponent(
                    context.adminConfig?.mall?.useCustomSmallCarousel,
                    <SmallCarousel editable={editable} size={size} />,
                    {
                        editable,
                        cx,
                        component: `CustomSmallCarousel`,
                    }
                )}
            </ContentArea>
            {size === `large` && (
                <>
                    <ContentArea className="navDest left relative" name="important" id="important">
                        <ClickableText
                            onClick={() => scrollerTo(`categories`, -80)}
                            sx={{ position: `absolute`, right: 0, top: 45, fontWeight: `400` }}
                        >
                            See All
                        </ClickableText>
                        <HomeHeader>POPULAR CATEGORIES</HomeHeader>
                    </ContentArea>
                    <ContentArea
                        style={{ maxHeight: `115px`, overflowY: `hidden` }}
                        size={size}
                        className="wrap between nopad extramargin"
                    >
                        {categories.slice(0, 6).map((a: string) => (
                            <CategoryBoxSmall
                                key={a}
                                top
                                width={iconSizes[size]}
                                size={size}
                                context={context}
                                cName={a}
                            />
                        ))}
                    </ContentArea>
                </>
            )}
            <ContentArea className="navDest left" name="featured" id="featured">
                <HomeHeader>FEATURED</HomeHeader>
            </ContentArea>
            <GridArea {...{ contentAreaWidth, storeCellsAcross }}>
                {getDisplayableCells(featured, storeCellsAcross, size).map((a, i) => (
                    <LogoBox
                        length={featured.length}
                        key={i}
                        index={i}
                        {...{ setStore, storeCellsAcross, size, ...a }}
                    />
                ))}
            </GridArea>
            {(promotedOffers?.length || !promotedOffers) && (
                <ContentArea className="navDest left" name="important" id="important">
                    <HomeHeader>SPECIAL OFFERS</HomeHeader>
                </ContentArea>
            )}
            {useCustomComponent(
                promotedOffers,
                <ContentArea className={`nowrap between noflex nopad scroll ${size}`}>
                    <ImagePop {...ipopprops} v="a" />
                    <ImagePop {...ipopprops} v="b" />
                    <ImagePop {...ipopprops} v="c" />
                    <ImagePop {...ipopprops} v="d" />
                </ContentArea>,
                { cx: { ...cx, setStore }, component: `CustomImagePopRow` }
            )}
            <ContentArea className="navDest left" name="popular" id="popular">
                <HomeHeader>POPULAR</HomeHeader>
            </ContentArea>
            <GridArea {...{ contentAreaWidth, storeCellsAcross }}>
                {getDisplayableCells(popular, storeCellsAcross, size).map((a, i) => (
                    <LogoBox
                        length={featured.length}
                        key={i}
                        index={i}
                        {...{ setStore, storeCellsAcross, size, ...a }}
                    />
                ))}
            </GridArea>
            {context.adminConfig?.mall?.showBigImage && (
                <ContentArea>
                    <BigImage size={size} editable={editable} />
                </ContentArea>
            )}
            <ContentArea
                style={{ background: theme.palette.account.background, marginTop: `60px` }}
                className="navDest left mtop"
                name="categories"
                id="categories"
            >
                <HomeHeader sx={{ marginTop: `15px` }}>CATEGORIES</HomeHeader>
            </ContentArea>
            <ContentArea style={{ background: theme.palette.account.background }} className="wrap nopad">
                {categories.map((a: string) => (
                    <CategoryBoxSmall
                        key={a}
                        margin={iconMargins}
                        width={iconSizes[size]}
                        size={size}
                        context={context}
                        cName={a}
                    />
                ))}
            </ContentArea>
        </AppWrap>
    ) : null;
};

export default Home;
